// frontend/src/components/report/LockedFindings.js
//
// The free tier's curiosity engine (2026-09-20). Two blocks:
//
//   1. The locked inventory — every finding TITLE, grouped by pillar and sorted
//      worst-first, with the severity tally as the headline. Evidence and remedy
//      are withheld (the backend never sends them on this tier — see
//      server.py's _findings_teaser, so there is nothing here to inspect in
//      devtools either).
//   2. One finding handed over COMPLETE — title + evidence + concrete fix +
//      effort band — as proof of the format the paid report uses, on the
//      reader's own business.
//
// Why this replaces the old `checklistTeaser` cards: those showed an abstract
// ratio ("Von 5 geprüften Kriterien erfüllen Sie nur 2"), depended on
// `score_checks` which was absent in 21 of 28 real free reports, and never named
// a single thing. The data for the version below was already being computed and
// German-translated on every scan, and `get_public_report` was throwing it away.
//
// Deliberately NOT a blur effect over real text — a locked item shows its title
// plainly and says what is missing. A blur reads as a paywall trick and invites
// people to try and defeat it; a plain lock reads as an honest boundary.
import { useState } from "react";
import { Lock, Unlock, ShieldAlert, Clock, TriangleAlert } from "lucide-react";
import { SEVERITY_CLS } from "./reportUtils";

const SEVERITY_ORDER = ["critical", "high", "medium", "low"];

const SHOWN_COLLAPSED = 3;

export function LockedFindings({ teaser, counts, sample, L, onCtaClick }) {
  const [expanded, setExpanded] = useState(false);
  if (!teaser?.length || !counts?.total) return null;

  // The sample is pulled OUT of the locked list — showing the same finding as
  // both "here it is in full" and "this one is locked" would read as a mistake.
  const locked = sample ? teaser.filter((f) => f.code !== sample.code) : teaser;

  // The severity tally in the header must count what's actually still locked,
  // not the pre-sample `counts` prop — otherwise the sample's own severity
  // stays in the tally after its item has already been shown in full above
  // (real bug, caught 2026-09-28: "1 Hoch" shown when the one high-severity
  // finding was the sample itself, so 0 high-severity items were left locked).
  const lockedCounts = locked.reduce((acc, f) => {
    acc[f.severity] = (acc[f.severity] || 0) + 1;
    return acc;
  }, {});

  // Group by pillar, keeping the backend's worst-first order within each group.
  const byPillar = Object.keys(L.pillars)
    .map((pillar) => ({
      pillar,
      label: L.pillars[pillar],
      items: locked.filter((f) => f.pillar === pillar),
    }))
    .filter((g) => g.items.length > 0);
  const renderItem = (f) => (
                    <li
                      key={f.code}
                      className="flex items-start gap-2.5 rounded-xl border bg-secondary/30 p-3.5"
                    >
                      {f.code === "no_website"
                        ? <TriangleAlert className="mt-1 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                        : <Lock className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${SEVERITY_CLS[f.severity] || SEVERITY_CLS.low}`}>
                            {L.severity[f.severity] || L.severity.low}
                          </span>
                          <span className="text-base font-medium">{f.title}</span>
                        </div>
                        {f.why && <p className="mt-1 text-sm text-muted-foreground">{f.why}</p>}
                        {f.code !== "no_website" && <p className="mt-1 text-xs text-muted-foreground">{L.lockedItemHint}</p>}
                      </div>
                    </li>
  );
  const sevIdx = (f) => SEVERITY_ORDER.indexOf(f.severity);
  const topLocked = locked
    .map((f, i) => ({ f, i }))
    .sort((a, b) => (sevIdx(a.f) < 0 ? 9 : sevIdx(a.f)) - (sevIdx(b.f) < 0 ? 9 : sevIdx(b.f)) || a.i - b.i)
    .slice(0, SHOWN_COLLAPSED)
    .map(({ f }) => f);

  return (
    <>
      {sample && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-green-300 bg-card shadow-sm">
          <div className="border-b border-green-200 bg-green-50 p-5 sm:p-6">
            <h2 className="mb-1.5 flex items-center gap-1.5 font-heading text-lg font-semibold text-green-900">
              <Unlock className="h-4 w-4" /> {L.sampleTitle}
            </h2>
            <p className="text-base text-green-900/80">{L.sampleDesc(counts.total)}</p>
          </div>
          <div className="p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${SEVERITY_CLS[sample.severity] || SEVERITY_CLS.low}`}>
                {L.severity[sample.severity] || L.severity.low}
              </span>
              <span className="text-sm text-muted-foreground">{L.pillars[sample.pillar]}</span>
            </div>
            <h3 className="mt-2 text-base font-semibold">{sample.title}</h3>

            <div className="mt-4 space-y-3">
              <div>
                <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {L.sampleEvidenceLabel}
                </span>
                <p className="mt-1 text-base text-muted-foreground">{sample.evidence}</p>
              </div>
              <div className="rounded-xl border border-green-200 bg-green-50 p-3.5">
                <span className="text-sm font-semibold uppercase tracking-wide text-green-900/70">
                  {L.sampleFixLabel}
                </span>
                <p className="mt-1 text-base text-green-900">{sample.fix}</p>
                {sample.effort && L.sampleEffort[sample.effort] && (
                  <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-green-900/80">
                    <Clock className="h-3.5 w-3.5" /> {L.sampleEffortLabel}: {L.sampleEffort[sample.effort]}
                  </p>
                )}
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">{L.sampleFooter}</p>
          </div>
        </div>
      )}

      {byPillar.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-2xl border bg-card shadow-sm">
          <div className="border-b bg-secondary/50 p-5 sm:p-6">
            <h2 className="mb-1.5 flex items-center gap-1.5 font-heading text-lg font-semibold">
              <ShieldAlert className="h-4 w-4" /> {L.lockedTitle(locked.length)}
            </h2>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {SEVERITY_ORDER.filter((s) => lockedCounts[s] > 0).map((s) => (
                <span key={s} className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${SEVERITY_CLS[s]}`}>
                  {lockedCounts[s]} {L.severity[s]}
                </span>
              ))}
            </div>
            <p className="text-base text-muted-foreground">{L.lockedDesc}</p>
          </div>

          <div className="divide-y">
            {expanded || locked.length <= SHOWN_COLLAPSED ? (
              byPillar.map((group) => (
                <div key={group.pillar} className="p-5 sm:p-6">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.label} · {group.items.length}
                  </h3>
                  <ul className="space-y-2">{group.items.map(renderItem)}</ul>
                </div>
              ))
            ) : (
              <div className="p-5 sm:p-6" id="locked-findings-list">
                <ul className="space-y-2">{topLocked.map(renderItem)}</ul>
              </div>
            )}
            {locked.length > SHOWN_COLLAPSED && (
              <div className="p-3 text-center print:hidden">
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls="locked-findings-list"
                  onClick={() => setExpanded((v) => !v)}
                  className="text-base font-semibold text-primary underline underline-offset-2 hover:opacity-80"
                  data-testid="locked-findings-toggle"
                >
                  {expanded ? L.showFewerPoints : L.showAllPoints(locked.length)}
                </button>
              </div>
            )}
          </div>

          {onCtaClick && (
            <div className="border-t bg-accent/40 p-4 text-center print:hidden">
              <button
                type="button"
                onClick={onCtaClick}
                className="text-base font-semibold text-primary underline underline-offset-2 hover:opacity-80"
                data-testid="locked-findings-cta"
              >
                {L.requestDetailed}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default LockedFindings;
